import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import apiClient from "../client";
import type { ApiError } from "../client";

/**
 * Both of these publish to the Back Office and keep nothing here, so there is no query to invalidate
 * and nothing for the portal to read back. The customer is told it landed and that is the whole
 * contract — see the epic's decision that v1 stops at capture.
 */

export interface AskQuestionRequest {
  strategyId: string;
  question: string;
}

export interface RateStrategyRequest {
  strategyId: string;
  stars: number;
  explanation: string;
  consentedToPublication: boolean;
  displayName: string;
}

export const QUESTION_MIN_LENGTH = 10;
export const QUESTION_MAX_LENGTH = 2000;
export const EXPLANATION_MAX_LENGTH = 2000;

export function useAskStrategyQuestion() {
  return useMutation({
    mutationFn: async ({ strategyId, question }: AskQuestionRequest) => {
      const response = await apiClient.post<{ questionId: string }>(
        `/trading-strategies/${strategyId}/questions`,
        { question },
      );
      return response.data;
    },
  });
}

export function useRateStrategy() {
  return useMutation({
    mutationFn: async ({ strategyId, ...rating }: RateStrategyRequest) => {
      const response = await apiClient.post<{ ratedAt: string }>(
        `/trading-strategies/${strategyId}/ratings`,
        rating,
      );
      return response.data;
    },
  });
}

/**
 * First name plus last initial, which is what the rating form offers before the customer edits it.
 * Falls back to whatever half exists rather than showing an empty box they have to fill from nothing.
 */
export function suggestedDisplayName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  const first = (firstName ?? "").trim();
  const initial = (lastName ?? "").trim().charAt(0);
  return [first, initial ? `${initial}.` : ""].filter(Boolean).join(" ");
}

/**
 * Publishing is fast enough that the confirmation can arrive before the customer has registered
 * pressing the button, which reads as nothing having happened. Holding the pending state for a
 * moment makes the send legible. Deliberate padding, not a workaround for a slow call.
 */
export const MINIMUM_PENDING_MS = 800;

export async function withMinimumDuration<T>(
  work: Promise<T>,
  ms: number = MINIMUM_PENDING_MS,
): Promise<T> {
  // A failure is not padded - Promise.all rejects as soon as the work does, so an error surfaces
  // immediately rather than being held back behind a spinner.
  const [result] = await Promise.all([
    work,
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]);
  return result;
}

/**
 * The submission endpoints are rate limited per customer, so a refusal here is a "wait", not a
 * "retry" — telling someone to try again when the server has just told them to slow down is the
 * wrong advice, and they will press the button again immediately.
 *
 * Reads `status` off the client's own ApiError, not `response.status`: the axios response
 * interceptor reshapes every failure into ApiError before it reaches a caller.
 */
export function isRateLimited(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as ApiError).status === 429
  );
}

/**
 * What the server allows per customer per window. Mirrored here so the portal can stop someone
 * before they hit the wall — being told "no" after writing a question is a poor way to learn there
 * was a limit. The server stays the authority; this only spares the wasted attempt, and
 * `isRateLimited` is the backstop when the two disagree.
 */
export const RATE_WINDOW_MS = 10 * 60 * 1000;
export const QUESTION_LIMIT = 5;
export const RATING_LIMIT = 15;

export type FeedbackKind = "questions" | "ratings";

const storageKey = (kind: FeedbackKind) => `scryer-feedback-sent-${kind}`;
const blockedKey = (kind: FeedbackKind) => `scryer-feedback-blocked-${kind}`;

const readBlockedUntil = (kind: FeedbackKind): number => {
  try {
    return Number(localStorage.getItem(blockedKey(kind))) || 0;
  } catch {
    return 0;
  }
};

const readStamps = (kind: FeedbackKind): number[] => {
  try {
    const raw = localStorage.getItem(storageKey(kind));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : [];
  } catch {
    // Private windows and blocked site data throw on access. An unknown allowance is not a reason to
    // block anyone - fall through to letting the server decide.
    return [];
  }
};

/**
 * Tracks this browser's own submissions inside the window, so the entry point can be disabled rather
 * than failing at the end of a form.
 */
export function useFeedbackAllowance(kind: FeedbackKind, limit: number) {
  const [nextAllowedAt, setNextAllowedAt] = useState<Date | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Recomputes from storage, then schedules itself for the moment the oldest submission ages out so
  // the button re-enables on its own. Every clock reading and every state update happens inside a
  // timer callback or an event handler - never in the render path or an effect body.
  const refresh = useCallback(() => {
    // Named inner function so the reschedule can refer to itself without the callback depending on
    // its own identity.
    function run() {
      const withinWindow = readStamps(kind).filter(
        (sentAt) => Date.now() - sentAt < RATE_WINDOW_MS,
      );

      // The server's refusal outranks the local count: this browser's tally can be short if storage
      // was cleared, or if submissions were made before it started counting.
      const serverBlock = readBlockedUntil(kind);
      const localBlock =
        withinWindow.length >= limit ? Math.min(...withinWindow) + RATE_WINDOW_MS : 0;
      const until = Math.max(serverBlock, localBlock);

      if (until <= Date.now()) {
        setNextAllowedAt(null);
        return;
      }

      setNextAllowedAt(new Date(until));

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(run, Math.max(until - Date.now(), 0) + 250);
    }

    run();
  }, [kind, limit]);

  useEffect(() => {
    const initial = setTimeout(refresh, 0);
    return () => {
      clearTimeout(initial);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [refresh]);

  const record = () => {
    const kept = readStamps(kind).filter((sentAt) => Date.now() - sentAt < RATE_WINDOW_MS);

    try {
      localStorage.setItem(storageKey(kind), JSON.stringify([...kept, Date.now()]));
    } catch {
      // Nothing to do - the server still enforces the real limit.
    }

    refresh();
  };

  /** The server said no. Believe it, so the button disables rather than inviting another rejection. */
  const blockUntil = (until: Date) => {
    try {
      localStorage.setItem(blockedKey(kind), String(until.getTime()));
    } catch {
      // Nothing to do - the message in the dialog still explains it.
    }

    refresh();
  };

  return { exhausted: nextAllowedAt !== null, nextAllowedAt, record, blockUntil };
}
